import React, { useEffect, useMemo, useState } from "react";
import { splitLabel } from "@/lib/splits";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, DollarSign, Search, Crown, ShieldCheck, RefreshCcw, Sparkles } from "lucide-react";

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

// The 21 canonical paid-role names the site admin wants pre-provisioned.
const PERK_ROLE_NAMES = [
  "Discord Crashout Loyal Members Role", "Discord Crashout Superstar Members Role", "Crashout Megastar Role",
  "Content Farm Loyal Members", "Content Farm Superstar Members", "Content Farm Megastar",
  "The Last Pour Podcast Loyal Members", "The Last Pour Podcast Superstars", "The Last Pour Megastar",
  "Clown Nine Loyal Members", "Clown Nine Superstar", "Clown Nine Megastar",
  "Toxic Tea Time Loyal Members", "Toxic Tea Time Superstars", "Toxic Tea Time Megastar",
  "Fastlane Loyal Members", "Fastlane Superstars", "Fastlane Megastar",
  "Into The Void Loyal Members", "Into The Void Superstars", "Into The Void Megastar",
];

/**
 * Admin: for each show, define up to 3 monthly-membership tiers with a
 * custom name, monthly price, and bulleted perks list. Each tier can be
 * linked to a Discord role — subscribers get that role automatically on
 * checkout and lose it on cancel.
 */
export default function MembershipsAdmin() {
  const [shows, setShows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [guildRoles, setGuildRoles] = useState([]);       // [{id, name, color}]
  const [seeding, setSeeding] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  const loadGuildRoles = async () => {
    setRolesLoading(true);
    try {
      const { data } = await api.get("/admin/discord/roles");
      setGuildRoles(data.roles || []);
    } catch (e) {
      // Non-fatal — dropdown just stays empty and admins can still paste role IDs.
      const detail = formatApiError(e.response?.data?.detail);
      if (detail && !detail.includes("not configured")) toast.error(`Role list: ${detail}`);
    } finally { setRolesLoading(false); }
  };

  const seedPerkRoles = async () => {
    if (!window.confirm(`Create the 21 canonical paid-membership roles on Discord?\n\nRoles that already exist by name will be reused. Discord rate-limits role creation, so if some fail with "Missing Permissions", wait ~15 min and click Retry Missing Roles.`)) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/discord/roles/create-many", { names: PERK_ROLE_NAMES });
      const parts = [`${data.created.length} created`, `${data.reused.length} reused`];
      if (data.failed.length) parts.push(`${data.failed.length} FAILED (retry later)`);
      toast.success(`Perk roles · ${parts.join(" · ")}`);
      await loadGuildRoles();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk create failed");
    } finally { setSeeding(false); }
  };

  const [bulkSeeding, setBulkSeeding] = useState(false);
  const bulkSeedShowsAndTiers = async () => {
    if (!window.confirm("Create/reuse the 7 canonical shows (Discord Crashout, Content Farm, The Last Pour Podcast, Clown Nine, Toxic Tea Time, Fastlane, Into The Void) with all 21 paid tiers auto-wired to their Discord roles?\n\nDefault prices: $2.99 / $6.99 / $14.99. You can edit any tier later.")) return;
    setBulkSeeding(true);
    try {
      const { data } = await api.post("/admin/memberships/seed-shows-and-tiers", {
        shows: [
          { title: "Discord Crashout" },
          { title: "Content Farm" },
          { title: "The Last Pour Podcast" },
          { title: "Clown Nine" },
          { title: "Toxic Tea Time" },
          { title: "Fastlane" },
          { title: "Into The Void" },
        ],
      });
      const newlyCreated = data.seeded.filter((s) => s.show_created).length;
      const gaps = data.seeded.reduce((n, s) => n + (s.role_gaps || []).length, 0);
      toast.success(`Seed complete · ${newlyCreated} new shows · 21 tiers synced · ${gaps} role gaps${gaps ? " (create missing Discord roles then re-run)" : ""}`);
      // Reload show list.
      const s = await api.get("/shows");
      setShows(s.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk seed failed");
    } finally { setBulkSeeding(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, sum] = await Promise.all([
          api.get("/shows"),
          api.get("/admin/memberships/summary"),
        ]);
        setShows(s.data || []);
        setSummary(sum.data);
        if ((s.data || []).length && !selected) setSelected(s.data[0].id);
      } catch (e) {
        toast.error("Failed to load shows");
      }
    })();
    loadGuildRoles();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/shows/${selected}/tiers`);
        const existing = data.tiers || [];
        // Pad to 3 tiers for editing convenience
        while (existing.length < 3) {
          existing.push({
            tier_order: existing.length + 1,
            name: "", price_cents: 0, perks: [], active: true, discord_role_id: "",
          });
        }
        setTiers(existing);
      } catch {
        setTiers([]);
      }
    })();
  }, [selected]);

  const filteredShows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return shows;
    return shows.filter((s) => (s.title || "").toLowerCase().includes(needle));
  }, [shows, q]);

  const current = shows.find((s) => s.id === selected);

  const setTier = (idx, patch) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const setPerk = (tIdx, pIdx, value) => {
    setTiers((prev) => prev.map((t, i) => {
      if (i !== tIdx) return t;
      const perks = [...(t.perks || [])];
      perks[pIdx] = value;
      return { ...t, perks };
    }));
  };

  const addPerk = (tIdx) => {
    setTiers((prev) => prev.map((t, i) => i === tIdx ? { ...t, perks: [...(t.perks || []), ""] } : t));
  };

  const removePerk = (tIdx, pIdx) => {
    setTiers((prev) => prev.map((t, i) => {
      if (i !== tIdx) return t;
      const perks = [...(t.perks || [])];
      perks.splice(pIdx, 1);
      return { ...t, perks };
    }));
  };

  const save = async () => {
    const payload = {
      tiers: tiers
        .filter((t) => (t.name || "").trim() && (t.price_cents || 0) > 0)
        .map((t) => ({
          name: t.name.trim(),
          price_cents: Number(t.price_cents),
          perks: (t.perks || []).map((p) => (p || "").trim()).filter(Boolean),
          active: t.active !== false,
          discord_role_id: (t.discord_role_id || "").trim(),
        })),
    };
    if (payload.tiers.length === 0) {
      toast.error("Add at least one tier with a name + price.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/shows/${selected}/tiers`, payload);
      toast.success("Tiers saved · Stripe prices synced");
      const existing = data.tiers || [];
      while (existing.length < 3) {
        existing.push({ tier_order: existing.length + 1, name: "", price_cents: 0, perks: [], active: true, discord_role_id: "" });
      }
      setTiers(existing);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="memberships-admin-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
            <Crown size={12} /> ▮ MEMBERSHIPS
          </div>
          <h2 className="font-display text-3xl">Per-Show Tiers</h2>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--ff-border)] mb-6" data-testid="memberships-summary">
          <StatBox label="Active subs" value={summary.counts.active} />
          <StatBox label="Total ever" value={summary.counts.total} />
          <StatBox label="Gross" value={money(summary.money_cents.gross)} />
          <StatBox label="Host cuts" value={money(summary.money_cents.host_cuts)} />
        </div>
      )}

      <PerkRolesLibrary
        guildRoles={guildRoles}
        seeding={seeding}
        rolesLoading={rolesLoading}
        onSeed={seedPerkRoles}
        onReload={loadGuildRoles}
        onBulkSeedShows={bulkSeedShowsAndTiers}
        bulkSeeding={bulkSeeding}
      />

      <div className="grid md:grid-cols-[280px_minmax(0,1fr)] gap-6">
        {/* Show sidebar */}
        <div className="surface-card p-4">
          <div className="relative mb-3">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search shows…"
              className="w-full pl-8 pr-3 py-2 text-sm"
              data-testid="memberships-show-search"
            />
          </div>
          <div className="space-y-px max-h-[560px] overflow-auto">
            {filteredShows.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s.id)}
                className={`w-full text-left px-3 py-2 font-mono-ui text-xs ${selected === s.id ? "bg-[var(--ff-red)] text-white" : "hover:bg-[var(--ff-surface-hover)] text-[var(--ff-text-dim)] hover:text-white"}`}
                data-testid={`memberships-show-pick-${s.id}`}
              >
                <div className="truncate">{s.title}</div>
                <div className="text-[0.6rem] uppercase tracking-[0.15em] opacity-70">
                  {(s.hosts || []).length} host{(s.hosts || []).length === 1 ? "" : "s"}
                </div>
              </button>
            ))}
            {filteredShows.length === 0 && (
              <div className="text-[var(--ff-text-dim)] font-mono-ui text-xs px-3 py-4">No shows.</div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div>
          {!current && (
            <div className="surface-card p-8 text-center text-[var(--ff-text-dim)] font-mono-ui text-sm">
              Pick a show to edit its tiers.
            </div>
          )}
          {current && (
            <div className="surface-card p-6" data-testid="memberships-editor">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-[var(--ff-border)]">
                <div>
                  <div className="font-display text-2xl">{current.title}</div>
                  <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-1">
                    Hosts: {(current.hosts || []).map((h) => h === (current.showrunner || (current.hosts || [])[0]) ? `★ ${h}` : h).join(" · ") || "—"}
                    {" · "}Split rule: {splitLabel((current.hosts || []).length, !!current.no_network_fee)}
                  </div>
                </div>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="memberships-save-btn">
                  <Save size={14} /> {saving ? "Saving…" : "Save Tiers"}
                </button>
              </div>

              <div className="space-y-6">
                {tiers.map((t, i) => (
                  <div key={i} className="border border-[var(--ff-border)] p-4" data-testid={`memberships-tier-editor-${i + 1}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="label-eyebrow text-[var(--ff-red)]">▮ TIER {i + 1}</div>
                      <label className="flex items-center gap-2 font-mono-ui text-xs">
                        <input
                          type="checkbox"
                          checked={t.active !== false}
                          onChange={(e) => setTier(i, { active: e.target.checked })}
                          data-testid={`memberships-tier-${i + 1}-active`}
                        /> Active
                      </label>
                    </div>
                    <div className="grid md:grid-cols-[minmax(0,2fr)_160px] gap-3 mb-3">
                      <input
                        value={t.name || ""}
                        onChange={(e) => setTier(i, { name: e.target.value })}
                        placeholder={`e.g. ${i === 0 ? "Bronze Fan" : i === 1 ? "Silver Supporter" : "Gold Producer"}`}
                        className="w-full px-3 py-2.5 text-sm"
                        data-testid={`memberships-tier-${i + 1}-name`}
                      />
                      <div className="relative">
                        <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ff-text-dim)]" />
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={t.price_cents ? (t.price_cents / 100).toFixed(2) : ""}
                          onChange={(e) => setTier(i, { price_cents: Math.round(Number(e.target.value || 0) * 100) })}
                          placeholder="4.99"
                          className="w-full pl-8 pr-3 py-2.5 text-sm"
                          data-testid={`memberships-tier-${i + 1}-price`}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="label-eyebrow mb-2 text-[var(--ff-text-dim)]">
                        DISCORD ROLE <span className="lowercase text-[0.55rem]">— auto-grant on subscribe, revoke on cancel</span>
                      </div>
                      <DiscordRolePicker
                        guildRoles={guildRoles}
                        showTitle={current?.title || ""}
                        tierName={t.name || ""}
                        value={t.discord_role_id || ""}
                        onChange={(next) => setTier(i, { discord_role_id: next })}
                        testId={`memberships-tier-${i + 1}-role-id`}
                      />
                    </div>
                    <div className="label-eyebrow mb-2 text-[var(--ff-text-dim)]">PERKS</div>
                    <div className="space-y-2">
                      {(t.perks || []).map((p, pi) => (
                        <div key={pi} className="flex gap-2">
                          <input
                            value={p}
                            onChange={(e) => setPerk(i, pi, e.target.value)}
                            placeholder="e.g. Members-only Discord role"
                            className="flex-1 px-3 py-2 text-sm"
                            data-testid={`memberships-tier-${i + 1}-perk-${pi}`}
                          />
                          <button
                            onClick={() => removePerk(i, pi)}
                            className="btn-ghost text-xs"
                            data-testid={`memberships-tier-${i + 1}-perk-${pi}-remove`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => addPerk(i)}
                        className="btn-ghost text-xs"
                        data-testid={`memberships-tier-${i + 1}-perk-add`}
                      >
                        <Plus size={12} /> Add Perk
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-[var(--ff-surface)] p-4">
      <div className="label-eyebrow text-[var(--ff-text-dim)] mb-1">{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}



function PerkRolesLibrary({ guildRoles, seeding, rolesLoading, onSeed, onReload, onBulkSeedShows, bulkSeeding }) {
  const [expanded, setExpanded] = useState(false);
  const byName = React.useMemo(() => {
    const m = new Map();
    guildRoles.forEach((r) => m.set(r.name.toLowerCase(), r));
    return m;
  }, [guildRoles]);
  const ready = PERK_ROLE_NAMES.filter((n) => byName.has(n.toLowerCase()));
  const missing = PERK_ROLE_NAMES.filter((n) => !byName.has(n.toLowerCase()));
  const total = PERK_ROLE_NAMES.length;
  return (
    <div className="surface-card p-5 mb-6" data-testid="perk-roles-library">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0">
          <div className="label-eyebrow text-[var(--ff-red)] mb-1 flex items-center gap-2">
            <ShieldCheck size={12} /> ▮ PAID PERK ROLES · DISCORD
          </div>
          <div className="font-display text-lg">
            {ready.length} of {total} paid roles ready on Discord
            {missing.length > 0 && <span className="text-[var(--ff-red)] text-xs font-mono-ui uppercase tracking-[0.15em] ml-2">· {missing.length} missing</span>}
          </div>
          <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
            {rolesLoading ? "Loading guild roles…" : `${guildRoles.length} total roles visible to the bot`}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={onReload} disabled={rolesLoading} className="btn-ghost text-xs" data-testid="perk-roles-refresh">
            <RefreshCcw size={12} /> Refresh
          </button>
          <button onClick={onSeed} disabled={seeding} className="btn-ghost text-xs" data-testid="perk-roles-seed">
            <Sparkles size={12} /> {seeding ? "Creating…" : missing.length > 0 ? `Create ${missing.length} Missing Roles` : "Re-run Bulk Create"}
          </button>
          <button onClick={onBulkSeedShows} disabled={bulkSeeding} className="btn-primary text-xs" data-testid="perk-roles-seed-shows">
            <Sparkles size={12} /> {bulkSeeding ? "Seeding…" : "Seed 7 Shows + 21 Tiers"}
          </button>
        </div>
      </div>
      {(missing.length > 0 || expanded) && (
        <div className="mt-4 pt-4 border-t border-[var(--ff-border)]">
          <button onClick={() => setExpanded((v) => !v)} className="btn-ghost text-[0.65rem] mb-3" data-testid="perk-roles-toggle-details">
            {expanded ? "Hide role breakdown" : "Show role breakdown"}
          </button>
          {expanded && (
            <div className="grid md:grid-cols-3 gap-x-6 gap-y-1 font-mono-ui text-[0.65rem]">
              {PERK_ROLE_NAMES.map((n) => {
                const r = byName.get(n.toLowerCase());
                return (
                  <div key={n} className={`flex items-center gap-2 py-0.5 ${r ? "text-green-300" : "text-yellow-400"}`} data-testid={`perk-role-status-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${r ? "bg-green-400" : "bg-yellow-400"}`} />
                    <span className="truncate">{n}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiscordRolePicker({ guildRoles, showTitle, tierName, value, onChange, testId }) {
  const [mode, setMode] = useState(value && !guildRoles.some((r) => r.id === value) ? "manual" : "pick");
  const options = React.useMemo(() => {
    if (!guildRoles?.length) return [];
    const showLower = (showTitle || "").toLowerCase();
    // Group: (1) roles matching the show title, (2) other perk-style roles, (3) everything else
    const showMatches = [];
    const perkish = [];
    const rest = [];
    guildRoles.forEach((r) => {
      const n = (r.name || "").toLowerCase();
      if (showLower && n.includes(showLower.split(" ")[0])) {
        showMatches.push(r);
      } else if (/loyal|superstar|megastar|founder|patron|member/.test(n)) {
        perkish.push(r);
      } else {
        rest.push(r);
      }
    });
    return [...showMatches, ...perkish, ...rest];
  }, [guildRoles, showTitle]);
  const selected = guildRoles.find((r) => r.id === value);

  // Auto-suggest: if role is unset and tier name matches "Loyal", "Superstar", or "Megastar",
  // hint the admin visually about the recommended role in the dropdown.
  const suggestion = React.useMemo(() => {
    if (!tierName || !showTitle) return null;
    const t = tierName.toLowerCase();
    const kind = /megastar/.test(t) ? "megastar" : /superstar/.test(t) ? "superstar" : /loyal/.test(t) ? "loyal" : null;
    if (!kind) return null;
    const showKey = showTitle.toLowerCase().split(" ")[0];
    const match = guildRoles.find((r) => {
      const n = (r.name || "").toLowerCase();
      return n.includes(showKey) && n.includes(kind);
    });
    return match || null;
  }, [tierName, showTitle, guildRoles]);

  return (
    <div className="space-y-2">
      {mode === "pick" ? (
        <>
          <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2.5 text-sm font-mono-ui bg-[var(--ff-surface)] border border-[var(--ff-border)]"
            data-testid={testId}
          >
            <option value="">— No Discord role linked —</option>
            {options.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <div className="flex items-center justify-between text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
            <span>
              {selected ? `Selected: ${selected.name} (${selected.id})` : options.length ? `${options.length} roles available` : "No guild roles loaded — click Refresh above"}
            </span>
            <button type="button" onClick={() => setMode("manual")} className="underline hover:text-white" data-testid={`${testId}-manual`}>
              Enter ID manually →
            </button>
          </div>
          {suggestion && suggestion.id !== value && (
            <button
              type="button"
              onClick={() => onChange(suggestion.id)}
              className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-red)] hover:underline"
              data-testid={`${testId}-suggest`}
            >
              ✦ Suggested: {suggestion.name} — click to apply
            </button>
          )}
        </>
      ) : (
        <>
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="e.g. 1234567890123456789"
            className="w-full px-3 py-2.5 text-sm font-mono-ui"
            data-testid={testId}
          />
          <div className="text-[0.6rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] text-right">
            <button type="button" onClick={() => setMode("pick")} className="underline hover:text-white" data-testid={`${testId}-picker`}>
              ← Back to role picker
            </button>
          </div>
        </>
      )}
    </div>
  );
}
